import React, { useEffect, useState, useMemo } from "react";
import { motion } from "framer-motion";
import {
  RefreshCw,
  BarChart3,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  CheckCircle2,
  CircleDot,
  XCircle,
  HelpCircle,
  FileText,
  Lock,
  TrendingUp,
  Download,
} from "lucide-react";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
  Legend,
  CartesianGrid,
} from "recharts";
import toast from "react-hot-toast";
import {
  card,
  microLabel,
  sectionTitle,
  accentBarSection,
  bodySecondary,
  motionStaggerChild,
  emptyState,
  iconSize,
} from "../../../../../lib/design-system";
import { cn } from "../../../../../lib/utils";
import {
  getProgramAttainment,
  lockAttainmentSnapshot,
  getAttainmentTrend,
  exportAttainmentXlsx,
  exportSARDocx,
} from "../../../../../services/program.service";
import AtrForm from "../AtrForm";

// ---------------------------------------------------------------------------
// Status visual metadata
// ---------------------------------------------------------------------------
const STATUS_META = {
  green: {
    label: "Meeting target",
    text: "text-emerald-600 dark:text-emerald-400",
    bg: "bg-emerald-50 dark:bg-emerald-900/30",
    border: "border-emerald-200 dark:border-emerald-700",
    bar: "#10b981",
    Icon: CheckCircle2,
  },
  amber: {
    label: "Close to target",
    text: "text-amber-600 dark:text-amber-400",
    bg: "bg-amber-50 dark:bg-amber-900/30",
    border: "border-amber-200 dark:border-amber-700",
    bar: "#f59e0b",
    Icon: CircleDot,
  },
  red: {
    label: "Below target",
    text: "text-red-600 dark:text-red-400",
    bg: "bg-red-50 dark:bg-red-900/30",
    border: "border-red-200 dark:border-red-700",
    bar: "#ef4444",
    Icon: XCircle,
  },
  no_data: {
    label: "No data yet",
    text: "text-gray-500 dark:text-gray-400",
    bg: "bg-gray-50 dark:bg-gray-800",
    border: "border-gray-200 dark:border-gray-700",
    bar: "#d1d5db",
    Icon: HelpCircle,
  },
  no_target: {
    label: "No target set",
    text: "text-gray-500 dark:text-gray-400",
    bg: "bg-gray-50 dark:bg-gray-800",
    border: "border-gray-200 dark:border-gray-700",
    bar: "#9ca3af",
    Icon: HelpCircle,
  },
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
const AttainmentTab = ({ programId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [atrPrefill, setAtrPrefill] = useState(null);

  // Trend state
  const [trendData, setTrendData] = useState(null);
  const [showTrend, setShowTrend] = useState(false);

  // Lock snapshot modal state
  const [showLockModal, setShowLockModal] = useState(false);
  const [lockYear, setLockYear] = useState("");
  const [locking, setLocking] = useState(false);

  // Export state
  const [exporting, setExporting] = useState(false);
  const [sarExporting, setSarExporting] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [result, trend] = await Promise.all([
        getProgramAttainment(programId),
        getAttainmentTrend(programId).catch(() => null),
      ]);
      setData(result);
      if (trend) setTrendData(trend);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load attainment data");
    } finally {
      setLoading(false);
    }
  };

  const handleSARExport = async () => {
    setSarExporting(true);
    try {
      const blob = await exportSARDocx(programId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `SAR_${programId}_${new Date().toISOString().slice(0, 10)}.docx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("SAR Word document downloaded");
    } catch {
      toast.error("SAR export failed — try again");
    } finally {
      setSarExporting(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await exportAttainmentXlsx(programId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `attainment_${programId}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Evidence export downloaded");
    } catch {
      toast.error("Export failed — try again");
    } finally {
      setExporting(false);
    }
  };

  const handleLockSnapshot = async () => {
    if (!lockYear.trim()) { toast.error("Enter an academic year (e.g. 2024-25)"); return; }
    setLocking(true);
    try {
      const result = await lockAttainmentSnapshot(programId, lockYear.trim());
      toast.success(`Snapshot locked: ${result.outcomesLocked} outcomes for ${lockYear}`);
      setShowLockModal(false);
      setLockYear("");
      // Reload trend
      const trend = await getAttainmentTrend(programId).catch(() => null);
      if (trend) { setTrendData(trend); setShowTrend(true); }
    } catch (err) {
      toast.error(err.response?.data?.error || "Lock failed");
    } finally {
      setLocking(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [programId]);

  const allOutcomes = useMemo(() => {
    if (!data) return [];
    return [...(data.pos || []), ...(data.psos || [])];
  }, [data]);

  const chartData = useMemo(
    () =>
      allOutcomes.map((o) => ({
        code: o.outcome.code,
        target: o.targetLevel != null ? Number(o.targetLevel) : 0,
        actual: o.actualLevel != null ? Number(o.actualLevel) : 0,
        status: o.status,
      })),
    [allOutcomes]
  );

  if (loading) return <SkeletonView />;
  if (error) return <ErrorView error={error} onRetry={fetchData} />;
  if (!data) return null;

  const summary = data.summary || {};

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
        <div>
          <h2 className={cn(sectionTitle, "text-xl sm:text-2xl")}>
            PO / PSO Attainment
          </h2>
          <div className={cn(accentBarSection, "mt-2")} />
          <p className={cn(bodySecondary, "mt-3 max-w-2xl")}>
            Direct: CO-tagged question scores (threshold 60%). Indirect: student
            Likert survey (avg/5 × 3). Combined = 0.8 × Direct + 0.2 × Indirect
            (falls back to Direct-only when no surveys). PO/PSO = weighted avg
            of CO combined levels × CO-PO strengths.
          </p>
        </div>
        <div className="flex items-center gap-2 self-start flex-wrap justify-end">
          {trendData?.years?.length > 0 && (
            <button
              onClick={() => setShowTrend((v) => !v)}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-sm border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-sm"
            >
              <TrendingUp className="h-4 w-4" />
              Trend
            </button>
          )}
          <button
            onClick={handleSARExport}
            disabled={sarExporting}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-sm bg-accent1 text-white hover:bg-accent1/90 transition-colors text-sm font-medium disabled:opacity-50"
            title="Download full NBA SAR Word document (.docx)"
          >
            <Download className="h-4 w-4" />
            {sarExporting ? "Generating…" : "SAR (.docx)"}
          </button>
          <button
            onClick={handleExport}
            disabled={exporting}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-sm border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-sm disabled:opacity-50"
            title="Download Excel evidence export"
          >
            <Download className="h-4 w-4" />
            {exporting ? "Exporting…" : "Excel"}
          </button>
          <button
            onClick={() => setShowLockModal(true)}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-sm border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-sm"
            title="Lock current attainment as year-end snapshot"
          >
            <Lock className="h-4 w-4" />
            Lock Year
          </button>
          <button
            onClick={fetchData}
            className="px-3 py-2 rounded-sm border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            aria-label="Refresh"
          >
            <RefreshCw className={iconSize.md} />
          </button>
        </div>
      </div>

      {/* Summary cards */}
      <SummaryCards summary={summary} />

      {/* Chart */}
      {chartData.length > 0 && (
        <div className={cn(card, "p-5 mb-8")}>
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 className="h-4 w-4 text-accent1" />
            <h3 className={microLabel}>Target vs Actual by Outcome</h3>
          </div>
          <ResponsiveContainer width="100%" height={320}>
            <BarChart
              data={chartData}
              margin={{ top: 8, right: 16, bottom: 8, left: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="code" stroke="#6b7280" fontSize={11} />
              <YAxis
                domain={[0, 3]}
                ticks={[0, 1, 2, 3]}
                stroke="#6b7280"
                fontSize={11}
              />
              <Tooltip cursor={{ fill: "rgba(0,0,0,0.05)" }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="target" name="Target" fill="#cbd5e1" />
              <Bar dataKey="actual" name="Actual">
                {chartData.map((d, i) => (
                  <Cell
                    key={`cell-${i}`}
                    fill={STATUS_META[d.status]?.bar || "#9ca3af"}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* List */}
      {allOutcomes.length === 0 ? (
        <div className={emptyState}>
          <BarChart3 className="h-7 w-7 mx-auto text-gray-400 mb-3" />
          <div className="font-bold text-gray-700 dark:text-gray-200">
            No POs or PSOs defined yet
          </div>
          <div className={cn(bodySecondary, "mt-1")}>
            Seed the 12 GAPC POs in the POs tab first.
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {allOutcomes.map((o, i) => (
            <OutcomeRow
              key={`${o.outcomeType}-${o.outcome._id}`}
              outcome={o}
              index={i}
              expanded={expandedId === o.outcome._id}
              onToggle={() =>
                setExpandedId(
                  expandedId === o.outcome._id ? null : o.outcome._id
                )
              }
              onDraftAtr={() => {
                setAtrPrefill({
                  outcomeType: o.outcomeType,
                  outcomeId: o.outcome._id,
                  observedAttainment: o.actualLevel,
                  targetAttainment: o.targetLevel,
                  // AI context — passed to suggestATRDraft
                  _aiContext: {
                    po_code: o.outcome.code,
                    po_statement: o.outcome.statement || "",
                    observed_level: o.actualLevel,
                    target_level: o.targetLevel,
                    contributing_cos: (o.contributingCOs || []).map((c) => ({
                      co_code: c.coCode,
                      co_level: c.combinedLevel ?? c.coLevel,
                      course_code: c.course?.courseCode || "",
                      mapping_strength: c.mappingStrength,
                    })),
                  },
                });
              }}
            />
          ))}
        </div>
      )}

      <div className="mt-6 text-xs text-gray-400 dark:text-gray-500">
        Last computed: {new Date(data.computedAt).toLocaleString()}
      </div>

      {/* ── 3-Year Trend Chart ──────────────────────────────────────── */}
      {showTrend && trendData && trendData.years.length > 0 && (
        <TrendChart trendData={trendData} />
      )}

      {/* ── Lock Year Modal ─────────────────────────────────────────── */}
      {showLockModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            className={cn(card, "w-full max-w-md p-6")}
          >
            <div className="flex items-center gap-3 mb-4">
              <Lock className="h-5 w-5 text-accent1" />
              <h3 className={sectionTitle}>Lock Year-End Snapshot</h3>
            </div>
            <p className={cn(bodySecondary, "text-sm mb-4")}>
              Saves the current PO/PSO attainment levels as a permanent record for this academic year.
              Required for NBA Criterion 8 (3-year trend evidence). Re-locking the same year overwrites the previous snapshot.
            </p>
            <input
              type="text"
              value={lockYear}
              onChange={(e) => setLockYear(e.target.value)}
              placeholder="e.g. 2024-25"
              className="w-full px-3 py-2 rounded-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm mb-4"
              onKeyDown={(e) => e.key === "Enter" && handleLockSnapshot()}
              autoFocus
            />
            <div className="flex justify-end gap-3">
              <button
                onClick={() => { setShowLockModal(false); setLockYear(""); }}
                className="px-4 py-2 rounded-sm border border-gray-200 dark:border-gray-700 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={handleLockSnapshot}
                disabled={locking || !lockYear.trim()}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-sm bg-accent1 text-white text-sm font-medium hover:bg-accent1/90 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Lock className="h-4 w-4" />
                {locking ? "Locking…" : "Lock Snapshot"}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {atrPrefill && (
        <AtrForm
          programId={programId}
          prefill={atrPrefill}
          onClose={(reload) => {
            setAtrPrefill(null);
            // No need to refresh attainment — ATR creation doesn't affect math.
          }}
        />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const SummaryCards = ({ summary }) => {
  const items = [
    { label: "POs", value: summary.totalPOs ?? 0 },
    { label: "PSOs", value: summary.totalPSOs ?? 0 },
    { label: "Courses", value: summary.totalCoursesInProgram ?? 0 },
    {
      label: "Meeting Target",
      value: summary.meetingTarget ?? 0,
      color: "text-emerald-600 dark:text-emerald-400",
    },
    {
      label: "Below Target",
      value: summary.belowTarget ?? 0,
      color: "text-red-600 dark:text-red-400",
    },
    {
      label: "No Data",
      value: summary.noData ?? 0,
      color: "text-gray-500 dark:text-gray-400",
    },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
      {items.map((i) => (
        <div key={i.label} className={cn(card, "p-4")}>
          <div className={microLabel}>{i.label}</div>
          <div
            className={cn(
              "mt-1 text-2xl sm:text-3xl font-black tracking-tight",
              i.color || "text-gray-900 dark:text-white"
            )}
          >
            {i.value}
          </div>
        </div>
      ))}
    </div>
  );
};

const formatLevel = (v) => {
  if (v == null) return "—";
  const num = Number(v);
  if (Number.isInteger(num)) return String(num);
  return num.toFixed(2).replace(/\.?0+$/, "");
};

const Stat = ({ label, value, color }) => (
  <div className="text-right min-w-[60px]">
    <div
      className={cn(
        "text-2xl font-black tracking-tight",
        color || "text-gray-900 dark:text-white"
      )}
    >
      {formatLevel(value)}
    </div>
    <div className={microLabel}>{label}</div>
  </div>
);

const OutcomeRow = ({ outcome, expanded, onToggle, onDraftAtr, index }) => {
  const meta = STATUS_META[outcome.status] || STATUS_META.no_data;
  const Icon = meta.Icon;
  // Draft ATR makes sense when there's actual data AND it's not green
  const showDraftAtr =
    outcome.actualLevel != null &&
    (outcome.status === "red" || outcome.status === "amber");

  return (
    <motion.div {...motionStaggerChild(index, 0)} className={cn(card, "p-5")}>
      <div className="flex items-start gap-4">
        <button
          onClick={onToggle}
          className="flex-shrink-0 mt-1"
          aria-label={expanded ? "Collapse" : "Expand"}
        >
          {expanded ? (
            <ChevronDown className="h-5 w-5 text-gray-400" />
          ) : (
            <ChevronRight className="h-5 w-5 text-gray-400" />
          )}
        </button>
        <button
          onClick={onToggle}
          className="flex-1 min-w-0 text-left"
        >
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <span className="px-2.5 py-1 rounded-sm bg-accent2 text-accent1 text-xs font-semibold">
              {outcome.outcome.code}
            </span>
            <span className="text-[10px] uppercase tracking-wider font-bold text-gray-400">
              {outcome.outcomeType}
            </span>
            {outcome.outcome.category && (
              <span className="text-xs font-bold text-gray-700 dark:text-gray-300">
                {outcome.outcome.category}
              </span>
            )}
          </div>
          <p className="text-sm text-gray-700 dark:text-gray-300 line-clamp-2">
            {outcome.outcome.statement}
          </p>
        </button>
        <div className="flex items-center gap-3 flex-shrink-0">
          <Stat label="Target" value={outcome.targetLevel} />
          <Stat
            label="Actual"
            value={outcome.actualLevel}
            color={meta.text}
          />
          <span
            className={cn(
              "inline-flex items-center gap-1 px-2.5 py-1 rounded-sm border text-xs font-medium whitespace-nowrap",
              meta.bg,
              meta.border,
              meta.text
            )}
          >
            <Icon className="h-3 w-3" />
            {meta.label}
          </span>
          {showDraftAtr && (
            <button
              onClick={onDraftAtr}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-sm bg-accent1 text-white text-xs font-semibold hover:opacity-90 transition-opacity whitespace-nowrap"
              title="Open an Action Taken Report pre-filled with this outcome's gap"
            >
              <FileText className="h-3 w-3" />
              Draft ATR
            </button>
          )}
        </div>
      </div>

      {expanded && (
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <ContributorsTable outcome={outcome} />
        </div>
      )}
    </motion.div>
  );
};

const ContributorsTable = ({ outcome }) => {
  if (!outcome.contributingCOs || outcome.contributingCOs.length === 0) {
    return (
      <div className="text-sm text-gray-500 dark:text-gray-400 italic">
        No contributing COs yet — either no CO-PO mappings exist for this{" "}
        {outcome.outcomeType}, or none of the mapped COs have graded data.
        Mapping count for this outcome: {outcome.mappingCount}.
      </div>
    );
  }

  // Group by course
  const byCourse = new Map();
  outcome.contributingCOs.forEach((c) => {
    const key = c.course._id.toString();
    if (!byCourse.has(key))
      byCourse.set(key, { course: c.course, cos: [] });
    byCourse.get(key).cos.push(c);
  });

  const totalWeightedSum = outcome.contributingCOs.reduce(
    (sum, c) => sum + c.weighted,
    0
  );
  const totalWeightSum = outcome.contributingCOs.reduce(
    (sum, c) => sum + c.mappingStrength,
    0
  );

  return (
    <div className="space-y-3">
      {[...byCourse.values()].map((g) => (
        <div
          key={g.course._id}
          className="p-3 rounded-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700"
        >
          <div className="text-xs font-bold text-gray-900 dark:text-white mb-2">
            {g.course.courseCode} · {g.course.title}
          </div>
          <div className="space-y-1">
            {g.cos.map((c, i) => (
              <div key={i} className="flex items-center justify-between text-xs gap-2">
                <span className="text-gray-700 dark:text-gray-300 flex-1">
                  <span className="font-semibold">{c.coCode}</span>
                  {" · "}
                  {c.combinedLevel != null ? (
                    <>
                      <span className="text-accent1 font-semibold">
                        Combined {Number(c.combinedLevel).toFixed(2)}
                      </span>
                      <span className="text-gray-400 ml-1">
                        (D:{c.directLevel ?? "—"}{" "}
                        {c.indirectLevel != null ? `I:${Number(c.indirectLevel).toFixed(2)}` : "no survey"})
                      </span>
                    </>
                  ) : (
                    <>Level {c.coLevel}</>
                  )}
                  {" × Strength "}{c.mappingStrength}
                </span>
                <span className="font-mono font-semibold text-gray-900 dark:text-white">
                  = {c.weighted}
                </span>
              </div>
            ))}
          </div>
        </div>
      ))}
      <div className="flex items-center justify-between text-xs pt-2 border-t border-gray-200 dark:border-gray-700">
        <span className="font-bold text-gray-700 dark:text-gray-300">
          Weighted sum / weight sum = {totalWeightedSum} / {totalWeightSum}
        </span>
        <span className="font-mono font-black text-accent1 text-base">
          ={" "}
          {totalWeightSum > 0
            ? (totalWeightedSum / totalWeightSum).toFixed(2)
            : "—"}
        </span>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Trend chart — multi-line, one line per PO/PSO across up to 3 years
// ---------------------------------------------------------------------------
const TREND_COLORS = [
  "#6366f1","#10b981","#f59e0b","#ef4444","#8b5cf6","#06b6d4",
  "#f97316","#84cc16","#ec4899","#14b8a6","#a855f7","#3b82f6",
];

const TrendChart = ({ trendData }) => {
  const { years, outcomes } = trendData;

  // Build recharts data: one object per year with a key per outcome code
  const chartData = years.map((y) => {
    const row = { year: y };
    outcomes.forEach((o) => {
      const point = o.data.find((d) => d.year === y);
      row[o.code] = point?.actualLevel != null ? Number(Number(point.actualLevel).toFixed(2)) : null;
      row[`${o.code}_target`] = point?.targetLevel != null ? Number(point.targetLevel) : null;
    });
    return row;
  });

  // Only show POs (to keep chart readable; PSOs would be a separate toggle)
  const poOutcomes = outcomes.filter((o) => o.type === "PO");

  return (
    <div className={cn(card, "p-5 mt-6")}>
      <div className="flex items-center gap-2 mb-4">
        <TrendingUp className="h-4 w-4 text-accent1" />
        <h3 className={microLabel}>3-Year PO Attainment Trend</h3>
        <span className={cn(bodySecondary, "text-xs ml-1")}>({years.join(", ")})</span>
      </div>
      {poOutcomes.length === 0 ? (
        <p className={cn(bodySecondary, "text-sm")}>No PO trend data available.</p>
      ) : (
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={chartData} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="year" stroke="#6b7280" fontSize={11} />
            <YAxis domain={[0, 3]} ticks={[0, 1, 2, 3]} stroke="#6b7280" fontSize={11} />
            <Tooltip
              formatter={(value, name) => [value != null ? Number(value).toFixed(2) : "—", name]}
            />
            <Legend wrapperStyle={{ fontSize: 10 }} />
            {poOutcomes.map((o, i) => (
              <Line
                key={o.code}
                type="monotone"
                dataKey={o.code}
                stroke={TREND_COLORS[i % TREND_COLORS.length]}
                strokeWidth={2}
                dot={{ r: 4 }}
                connectNulls={false}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      )}
      <p className={cn(bodySecondary, "text-xs mt-2")}>
        Lock more academic years via "Lock Year" to build the trend. NBA requires 3 years of evidence under Criterion 8.
      </p>
    </div>
  );
};

const SkeletonView = () => (
  <div className="space-y-4">
    <div className="h-8 w-1/3 bg-gray-200 dark:bg-gray-700 rounded-sm animate-pulse" />
    <div className="grid grid-cols-3 lg:grid-cols-6 gap-3">
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <div
          key={i}
          className="h-16 bg-gray-100 dark:bg-gray-800 rounded-sm animate-pulse"
        />
      ))}
    </div>
    <div className="h-64 bg-gray-100 dark:bg-gray-800 rounded-sm animate-pulse" />
    {[0, 1, 2, 3].map((i) => (
      <div
        key={i}
        className="h-20 bg-gray-100 dark:bg-gray-800 rounded-sm animate-pulse"
      />
    ))}
  </div>
);

const ErrorView = ({ error, onRetry }) => (
  <div
    className={cn(
      emptyState,
      "border-red-200 dark:border-red-700"
    )}
  >
    <AlertTriangle className="h-7 w-7 mx-auto text-red-400 mb-3" />
    <div className="font-bold text-red-600 dark:text-red-400">{error}</div>
    <button
      onClick={onRetry}
      className="mt-3 text-sm underline text-gray-600 dark:text-gray-300"
    >
      Retry
    </button>
  </div>
);

export default AttainmentTab;
